import React, { useState, useImperativeHandle, useContext, useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';
import { usePopper } from 'react-popper';
import maxSizeModifier from 'popper-max-size-modifier';
import { useLayeringContext, ThemeContext, getInverseTheme, LayeringProvider, ThemeProvider } from '@rescui/ui-contexts';
import FocusLock from 'react-focus-lock';
import ReactDOM from 'react-dom';
import { useDropdownState, DROPDOWN_STATES } from './use-dropdown-state.js';
import { useScrollableContainer } from './use-scrollable-container.js';
import styles from './index.p.module.css.js';
import { applyMaxSize, sameWidthModifier } from './custom-modifiers.js';
import { extractThemeFromCss, extractPrimaryColors } from './css-vars-utils.js';

function getPopperOptions(_ref) {
  let {
    placement,
    maxHeight,
    leastMaxHeight,
    maxWidth,
    minWidth
  } = _ref;
  return {
    placement,
    modifiers: [{
      name: 'hide',
      enabled: false
    }, {
      name: 'flip',
      enabled: true
    }, {
      name: 'offset',
      enabled: true,
      options: {
        offset: [0, 8]
      }
    }, maxSizeModifier, { ...applyMaxSize,
      options: {
        maxHeight,
        leastMaxHeight
      }
    }, { ...sameWidthModifier,
      options: {
        maxWidth,
        minWidth
      }
    }]
  };
}

const Portal = _ref2 => {
  let {
    children,
    target = document.body
  } = _ref2;
  return /*#__PURE__*/ReactDOM.createPortal(children, target);
};

function useOnOutsideClick(_ref3) {
  let {
    onOutsideClick,
    isEnabled,
    ignoreElements = []
  } = _ref3;
  const ignoreElementRefsExists = ignoreElements.length > 0 && ignoreElements.every(Boolean);
  const handleDocumentClick = useCallback(e => {
    for (const element of ignoreElements) {
      if (!element || e.target === element || element.contains(e.target)) {
        return;
      }
    }

    onOutsideClick();
  }, [onOutsideClick, ...ignoreElements]); // eslint-disable-next-line consistent-return

  useEffect(() => {
    if (isEnabled && ignoreElementRefsExists) {
      document.addEventListener('click', handleDocumentClick, true);
      return () => document.removeEventListener('click', handleDocumentClick, true);
    }
  }, [handleDocumentClick, isEnabled, ignoreElementRefsExists]);
}

function useKeyPress(_ref4) {
  let {
    key,
    onKeyPress,
    isEnabled
  } = _ref4;
  useEffect(() => {
    if (!isEnabled) {
      return;
    }

    const keyDownHandler = e => {
      if (e.key === key) {
        e.preventDefault();
        onKeyPress();
      }
    };

    document.addEventListener('keydown', keyDownHandler, true); // eslint-disable-next-line consistent-return

    return () => {
      document.removeEventListener('keydown', keyDownHandler, true);
    };
  }, [key, onKeyPress, isEnabled]);
}

const THEME_STYLES = {
  light: styles.themeLight,
  dark: styles.themeDark
};
const MODE_STYLES = {
  classic: styles.modeClassic,
  rock: styles.modeRock
};

const DropdownRender = (_ref5, externalRef) => {
  let {
    children,
    trigger,
    theme: themeFromProps,
    isOpen = false,
    placement = 'bottom-start',
    mode = 'classic',
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    onRequestClose = () => {},
    minWidth,
    maxWidth,
    maxHeight,
    leastMaxHeight,
    cancelCloseAnimation = false,
    className,
    closeOnEsc = true,
    closeOnTab = false,
    disableFocusLock = false,
    renderInTrigger = false
  } = _ref5;
  const [popperRef, setPopperRef] = useState(null);
  const [triggerRef, setTriggerRef] = useState(null);
  const {
    setContentEl,
    scrollableStyles
  } = useScrollableContainer();
  useOnOutsideClick({
    isEnabled: isOpen,
    onOutsideClick: onRequestClose,
    ignoreElements: [popperRef, triggerRef]
  });
  useKeyPress({
    key: 'Escape',
    isEnabled: isOpen && closeOnEsc,
    onKeyPress: onRequestClose
  });
  useKeyPress({
    key: 'Tab',
    isEnabled: isOpen && closeOnTab,
    onKeyPress: onRequestClose
  });
  const [handleAnimationEnd, dropdownState] = useDropdownState(isOpen, cancelCloseAnimation);
  const {
    styles: popperStyles,
    attributes,
    update
  } = usePopper(triggerRef, popperRef, getPopperOptions({
    placement,
    maxHeight,
    leastMaxHeight,
    minWidth,
    maxWidth
  }));
  useImperativeHandle(externalRef, () => ({
    scheduleUpdate: () => {
      if (update) {
        update();
      }
    }
  }));
  const {
    dropdownZIndex
  } = useLayeringContext();
  const triggerRenderer = typeof trigger === 'function' ? trigger({
    ref: setTriggerRef
  }) : /*#__PURE__*/React.createElement("span", {
    "data-test": "dropdown-trigger",
    className: styles.triggerWrapper,
    ref: setTriggerRef
  }, trigger);
  const dropdownShouldBeRendered = dropdownState !== DROPDOWN_STATES.CLOSE;
  const wrapperShouldBeHidden = dropdownState === DROPDOWN_STATES.CLOSING;
  let primaryColors = {};
  let themeFromCss = 'light';

  if (triggerRef && dropdownShouldBeRendered) {
    // In theory this part of the code might cause issues with SSR.
    // But in this specific context, because of the nature of the state-ref "triggerRef",
    // we are sure that the dropdown wouldn't be rendered on the first client render.
    // Therefore, the first render on the client will be the same as on the server.
    const triggerStyles = getComputedStyle(triggerRef);
    themeFromCss = extractThemeFromCss(triggerStyles);
    primaryColors = extractPrimaryColors(triggerStyles);
  }

  const {
    theme: themeFromContext,
    noProvider
  } = useContext(ThemeContext);
  const theme = themeFromProps || (noProvider ? themeFromCss : themeFromContext);
  const childrenTheme = mode === 'rock' ? getInverseTheme(theme) : theme;
  return /*#__PURE__*/React.createElement(React.Fragment, null, triggerRenderer, dropdownShouldBeRendered && /*#__PURE__*/React.createElement(Portal, {
    target: renderInTrigger ? triggerRef : undefined
  }, /*#__PURE__*/React.createElement("div", {
    ref: setPopperRef,
    style: {
      maxHeight,
      ...popperStyles.popper,
      zIndex: dropdownZIndex,
      ...primaryColors
    },
    className: cn(styles.dropdown, className, MODE_STYLES[mode], THEME_STYLES[theme]),
    "data-test": "dropdown",
    ...attributes.popper
  }, /*#__PURE__*/React.createElement(LayeringProvider, {
    baseLayer: 'dropdown'
  }, /*#__PURE__*/React.createElement("div", {
    style: scrollableStyles,
    className: cn(styles.contentWrapper, wrapperShouldBeHidden && styles.wrapperHidden),
    onTransitionEnd: handleAnimationEnd,
    onAnimationEnd: handleAnimationEnd,
    "data-test": "dropdown-content"
  }, /*#__PURE__*/React.createElement(ThemeProvider, {
    ignoreThemeValueForCSSApi: noProvider,
    theme: childrenTheme
  }, /*#__PURE__*/React.createElement(FocusLock, {
    className: THEME_STYLES[childrenTheme],
    ref: setContentEl,
    disabled: dropdownState !== DROPDOWN_STATES.OPEN || disableFocusLock,
    returnFocus: {
      preventScroll: true
    },
    focusOptions: {
      preventScroll: true
    }
  }, children)))))));
};
/* eslint-enable react/prop-types */


const Dropdown = /*#__PURE__*/React.forwardRef(DropdownRender);
Dropdown.propTypes = {
  trigger: PropTypes.oneOfType([PropTypes.element, PropTypes.func]).isRequired,
  isOpen: PropTypes.bool.isRequired,
  onRequestClose: PropTypes.func,
  theme: PropTypes.oneOf(['light', 'dark']),
  mode: PropTypes.oneOf(['classic', 'rock']),
  placement: PropTypes.oneOf(['auto-start', 'auto', 'auto-end', 'top-start', 'top', 'top-end', 'right-start', 'right', 'right-end', 'bottom-end', 'bottom', 'bottom-start', 'left-end', 'left', 'left-start']),
  minWidth: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  maxWidth: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  maxHeight: PropTypes.number,
  leastMaxHeight: PropTypes.number
};
export { Dropdown, Dropdown as default };
